import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { canManageTeam, canAccessSettings, canViewAllCustomers, isOwner } from '../utils/permissions'
import { isOnboardingComplete } from '../utils/onboarding'
import WelcomeModal from '../components/WelcomeModal'

function Dashboard() {
  const { user, contractor, hasActiveSubscription, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [teamCount, setTeamCount] = useState(0)
  const [assessmentCount, setAssessmentCount] = useState(0)
  const [monthlyCount, setMonthlyCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)

  // Calculate days left in trial
  const getDaysLeftInTrial = () => {
    if (!contractor?.trial_ends_at) return 0
    const trialEndsAt = new Date(contractor.trial_ends_at)
    const now = new Date()
    const daysLeft = Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24))
    return Math.max(0, daysLeft)
  }

  const isTrialing = contractor?.subscription_status === 'trialing'
  const daysLeft = getDaysLeftInTrial()
  const isActive = hasActiveSubscription()

  // Check if user needs to see welcome modal (first login)
  useEffect(() => {
    if (contractor && isOwner(user)) {
      // Check if onboarding is incomplete AND user hasn't seen welcome modal
      const hasSeenWelcome = localStorage.getItem(`welcome_seen_${user.id}`)
      const needsOnboarding = !isOnboardingComplete(contractor)

      if (needsOnboarding && !hasSeenWelcome) {
        setShowWelcomeModal(true)
      }
    }
  }, [contractor, user])

  // Fetch dashboard statistics
  useEffect(() => {
    if (user?.contractor_id) {
      fetchDashboardStats()
    }
  }, [user])

  const fetchDashboardStats = async () => {
    try {
      setLoading(true)

      // Fetch team member count
      const { count: teamMemberCount, error: teamError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('contractor_id', user.contractor_id)

      if (teamError) throw teamError
      setTeamCount(teamMemberCount || 0)

      // Fetch total assessment count
      const { count: totalAssessments, error: assessmentError } = await supabase
        .from('assessments')
        .select('*', { count: 'exact', head: true })
        .eq('contractor_id', user.contractor_id)

      if (assessmentError) throw assessmentError
      setAssessmentCount(totalAssessments || 0)

      // Fetch this month's assessment count
      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      startOfMonth.setHours(0, 0, 0, 0)

      const { count: monthAssessments, error: monthError } = await supabase
        .from('assessments')
        .select('*', { count: 'exact', head: true })
        .eq('contractor_id', user.contractor_id)
        .gte('created_at', startOfMonth.toISOString())

      if (monthError) throw monthError
      setMonthlyCount(monthAssessments || 0)

    } catch (err) {
      console.error('Error fetching dashboard stats:', err)
    } finally {
      setLoading(false)
    }
  }

  // Handle welcome modal actions
  const handleStartSetup = () => {
    localStorage.setItem(`welcome_seen_${user.id}`, 'true')
    setShowWelcomeModal(false)
    navigate('/settings?onboarding=true')
  }

  const handleSkipSetup = () => {
    localStorage.setItem(`welcome_seen_${user.id}`, 'true')
    setShowWelcomeModal(false)
  }

  return (
    <>
      {showWelcomeModal && (
        <WelcomeModal
          onStart={handleStartSetup}
          onSkip={handleSkipSetup}
        />
      )}

    <div className="container" style={{ paddingTop: '40px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <header style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '5px' }}>
            {contractor?.company_logo_url && (
              <img
                src={contractor.company_logo_url}
                alt={contractor.company_name || 'Company Logo'}
                style={{
                  maxWidth: '60px',
                  maxHeight: '60px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  padding: '6px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <h1 style={{ fontSize: '28px', margin: 0 }}>Dashboard</h1>
          </div>
          <p style={{ color: '#7f8c8d' }}>
            Welcome back! Here's an overview of your HVAC assessment platform.
          </p>
        </header>

        {/* Trial/Subscription Status Banner - Only show to owners */}
        {isOwner(user) && isTrialing && daysLeft > 0 && daysLeft <= 3 && (
          <div className="mobile-stack" style={{
            padding: '16px 20px',
            background: '#fef2f2',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                ⚠️ Trial Ending Soon
              </div>
              <div style={{ fontSize: '14px', color: '#b91c1c' }}>
                Your trial ends in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}. Add payment details to continue using Krinos.
              </div>
            </div>
            <Link to="/settings" className="btn btn-primary mobile-full-width" style={{ whiteSpace: 'nowrap' }}>
              Update Billing
            </Link>
          </div>
        )}

        {isOwner(user) && !isActive && (
          <div style={{
            padding: '16px 20px',
            background: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}>
              🚫 Subscription Inactive
            </div>
            <div style={{ fontSize: '14px', color: '#b91c1c', marginBottom: '12px' }}>
              {!contractor?.stripe_customer_id
                ? 'Complete payment setup to activate your subscription and start creating assessments.'
                : 'Your trial has expired or subscription is inactive. Please update your billing information to continue creating assessments.'}
            </div>
            {!contractor?.stripe_customer_id ? (
              <button
                onClick={async () => {
                  try {
                    const response = await fetch('/.netlify/functions/create-checkout-session', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        email: user.email,
                        contractorId: contractor.id
                      })
                    });

                    console.log('Response status:', response.status);
                    const data = await response.json();
                    console.log('Response data:', data);

                    if (!response.ok) {
                      alert(`Error: ${data.message || data.error || 'Failed to create checkout session'}`);
                      return;
                    }

                    if (data.url) {
                      window.location.href = data.url;
                    } else {
                      alert('No checkout URL received from server');
                    }
                  } catch (err) {
                    console.error('Error creating checkout:', err);
                    alert(`Failed to create checkout: ${err.message}`);
                  }
                }}
                className="btn btn-primary"
              >
                Complete Payment Setup
              </button>
            ) : (
              <Link to="/settings" className="btn btn-primary">
                Update Billing
              </Link>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
          <div className="card">
            <h3 style={{ fontSize: '16px', color: '#7f8c8d', marginBottom: '8px' }}>Total Assessments</h3>
            <p style={{ fontSize: '32px', fontWeight: '600', color: '#2c3e50' }}>
              {loading ? '...' : assessmentCount}
            </p>
          </div>

          <div className="card">
            <h3 style={{ fontSize: '16px', color: '#7f8c8d', marginBottom: '8px' }}>This Month</h3>
            <p style={{ fontSize: '32px', fontWeight: '600', color: '#2c3e50' }}>
              {loading ? '...' : monthlyCount}
            </p>
          </div>

          <div className="card">
            <h3 style={{ fontSize: '16px', color: '#7f8c8d', marginBottom: '8px' }}>Team Members</h3>
            <p style={{ fontSize: '32px', fontWeight: '600', color: '#2c3e50' }}>
              {loading ? '...' : teamCount}
            </p>
          </div>
        </div>

        {/* Admin Quick Access */}
        {isAdmin() && (
          <div className="card" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #fbbf24', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', marginBottom: '20px', color: '#92400e' }}>⚡ Admin Access</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              <Link to="/admin" className="btn btn-primary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>🛠️</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Admin Dashboard</div>
              </Link>
              <Link to="/admin/rebates" className="btn btn-secondary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px' }}>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>💰</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Manage Rebates</div>
              </Link>
              <Link to="/admin/analytics" className="btn btn-secondary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px' }}>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>📊</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Analytics</div>
              </Link>
            </div>
          </div>
        )}

        <div className="card">
          <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>Quick Actions</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <Link to="/assessment" className="btn btn-primary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '28px', marginBottom: '6px' }}>🔍</div>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>Create New Assessment</div>
            </Link>
            <Link to="/assessments" className="btn btn-secondary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '28px', marginBottom: '6px' }}>📋</div>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>View Assessments</div>
            </Link>
            {canViewAllCustomers(user) && (
              <Link to="/customers" className="btn btn-secondary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px' }}>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>👤</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>View Customers</div>
              </Link>
            )}
            {canManageTeam(user) && (
              <Link to="/team" className="btn btn-secondary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px' }}>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>👥</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Manage Team</div>
              </Link>
            )}
            {canAccessSettings(user) && (
              <Link to="/settings" className="btn btn-secondary" style={{ textDecoration: 'none', textAlign: 'center', padding: '16px' }}>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>⚙️</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Settings</div>
              </Link>
            )}
          </div>
        </div>

      </div>
    </div>
    </>
  )
}

export default Dashboard
